import type { Editor } from '@tiptap/react';

import { Box, Button, Flex } from '@chakra-ui/react';
import { useEditorState } from '@tiptap/react';
import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  closeMessageMarkdownSlashMenu,
  createMessageMarkdownSlashCommands,
  filterMessageMarkdownSlashCommands,
  INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE,
  MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY,
  runMessageMarkdownSlashCommand,
  setMessageMarkdownSlashMenuSelectedIndex,
} from '#components/message-markdown-slash-menu/message-markdown-slash-menu';

const MESSAGE_MARKDOWN_SLASH_MENU_WIDTH = 240;

export function MessageMarkdownSlashMenuPopover({
  editor,
}: {
  editor: Editor | null;
}) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const slashMenuState =
    useEditorState({
      editor,
      selector: ({ editor: currentEditor }) =>
        currentEditor
          ? (MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY.getState(
              currentEditor.state,
            ) ?? INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE)
          : INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE,
    }) ?? INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE;

  const commands = useMemo(
    () => (editor ? createMessageMarkdownSlashCommands(editor) : []),
    [editor],
  );
  const filteredCommands = useMemo(
    () =>
      filterMessageMarkdownSlashCommands(commands, slashMenuState.query ?? ''),
    [commands, slashMenuState.query],
  );

  const position = useMemo(() => {
    if (!editor || !slashMenuState.active || !slashMenuState.range) {
      return null;
    }

    try {
      const coords = editor.view.coordsAtPos(slashMenuState.range.to);
      const pad = 8;
      const width = MESSAGE_MARKDOWN_SLASH_MENU_WIDTH;
      return {
        left: Math.max(
          pad,
          Math.min(coords.left, window.innerWidth - width - pad),
        ),
        top: Math.max(
          pad,
          Math.min(coords.bottom + 4, window.innerHeight - 220 - pad),
        ),
      };
    } catch {
      return null;
    }
  }, [editor, slashMenuState.active, slashMenuState.range]);

  useEffect(() => {
    if (!editor || !slashMenuState.active) {
      return;
    }

    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (menuRef.current?.contains(target)) {
        return;
      }

      closeMessageMarkdownSlashMenu(editor);
    };

    document.addEventListener('mousedown', onDocumentMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown, true);
    };
  }, [editor, slashMenuState.active]);

  if (
    !editor ||
    !slashMenuState.active ||
    !slashMenuState.range ||
    filteredCommands.length === 0 ||
    !position
  ) {
    return null;
  }

  const commandRange = slashMenuState.range;

  return createPortal(
    <Box
      ref={menuRef}
      className="message-markdown-slash-menu"
      role="toolbar"
      aria-label="Message markdown slash menu"
      position="fixed"
      zIndex={100}
      left={`${position.left}px`}
      top={`${position.top}px`}
      minW={`${MESSAGE_MARKDOWN_SLASH_MENU_WIDTH}px`}
      maxW={`min(${MESSAGE_MARKDOWN_SLASH_MENU_WIDTH}px, calc(100vw - 16px))`}
      maxH="220px"
      overflowY="auto"
    >
      <Flex direction="column" p={1}>
        {filteredCommands.map((command, index) => {
          const Icon = command.icon;

          return (
            <Button
              key={command.id}
              justifyContent="flex-start"
              variant="ghost"
              className="message-markdown-slash-menu__button"
              data-active={
                index === slashMenuState.selectedIndex ? 'true' : 'false'
              }
              onMouseEnter={() =>
                setMessageMarkdownSlashMenuSelectedIndex(editor, index)
              }
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() =>
                runMessageMarkdownSlashCommand(editor, command, commandRange)
              }
            >
              <Icon aria-hidden />
              {command.label}
            </Button>
          );
        })}
      </Flex>
    </Box>,
    document.body,
  );
}
